import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccessDenied from "@/components/AccessDenied";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator } from "@/components/ui/select";
import { Users, Sparkles, FileText, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import CustomTemplatePanel from "@/components/CustomTemplatePanel";

import { ReportStyleOptions, CUSTOM_STYLE, styleTriggerLabel } from "@/components/ReportStyleOptions";

const LENGTHS = ["~1 page", "2-5 pages", "5-10 pages"];
const canViewTeam = (u) => u?.account_type === "organization" && u?.can_view_team === true;

export default function TeamReports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [cfg, setCfg] = useState({ style: "Executive summary", length: "2-5 pages", title: "" });
  const [customTpl, setCustomTpl] = useState(null);
  const [includeScope, setIncludeScope] = useState(true);
  const [groupByTitle, setGroupByTitle] = useState(false);
  const [compiling, setCompiling] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/team/reports");
      setData(res.data);
    }
    catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { if (canViewTeam(user)) load(); }, [user]);

  if (user && !canViewTeam(user)) return <AccessDenied />;

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const compile = async () => {
    if (selected.length === 0) { toast.error(t("teamReports.selectAtLeastOne")); return; }
    setCompiling(true);
    try {
      const res = await api.post("/team/compile", { report_ids: selected, style: cfg.style, length: cfg.length, title: cfg.title || undefined, include_scope: includeScope, ...(cfg.style === CUSTOM_STYLE && customTpl ? customTpl : {}) });
      toast.success(t("teamReports.compiled"));
      navigate("/app/reports", { state: { openReportId: res.data.id } });
    } catch (err) { toast.error(apiError(err)); }
    finally { setCompiling(false); }
  };

  if (!data) return <p className="text-slatebody">{t("common.loading")}</p>;
  const all = [...data.own, ...data.team];
  const ladder = data.title_ladder || [];
  const NONE = "__none";
  const rankVal = (k) => {
    if (k === NONE) return Number.MAX_SAFE_INTEGER;
    const i = ladder.findIndex((x) => x.toLowerCase() === k.toLowerCase());
    return i === -1 ? Number.MAX_SAFE_INTEGER - 1 : ladder.length - 1 - i;
  };
  const titleGroups = () => {
    const groups = {};
    data.team.forEach((r) => {
      const key = r.author_title || NONE;
      (groups[key] = groups[key] || []).push(r);
    });
    const keys = Object.keys(groups).sort((a, b) => rankVal(a) - rankVal(b));
    return { groups, keys };
  };
  const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const exportRollupCsv = () => {
    const { groups, keys } = titleGroups();
    const headers = [t("teamReports.csvTitle"), t("teamReports.csvCount"), t("teamReports.csvReport"), t("teamReports.csvAuthor"), t("teamReports.csvUpdated")];
    const rows = [headers.map(csvCell).join(",")];
    keys.forEach((k) => {
      const label = k === NONE ? t("teamReports.noTitle") : k;
      groups[k].forEach((r) => {
        rows.push([label, groups[k].length, r.title, r.author_name || "", (r.updated_at || "").slice(0, 10)].map(csvCell).join(","));
      });
    });
    const blob = new Blob(["\uFEFF" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "team-reports-by-title.csv";
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(t("teamReports.csvExported"));
  };

  const Item = ({ r }) => (
    <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 cursor-pointer hover:border-[#C5C5D0] transition-colors" data-testid={`team-report-${r.id}`}>
      <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`team-check-${r.id}`} />
      <div className="h-9 w-9 rounded-md bg-accent flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-[#202123]" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-navy truncate">{r.title}</p>
        <p className="font-mono text-[11px] text-slatebody mt-0.5">{r.author_name}{r.author_title ? ` (${r.author_title})` : ""} · {r.source === "own" ? t("teamReports.you") : t("teamReports.team")}{r.is_compiled ? ` · ${t("teamReports.rollup")}` : ""}</p>
      </div>
    </label>
  );

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy flex items-center gap-2"><Users className="h-7 w-7 text-[#202123]" /> {t("teamReports.title")}</h1>
        <p className="text-slatebody mt-1 max-w-2xl">{t("teamReports.subtitle")}</p>
        <p className="text-xs text-slatebody mt-2" data-testid="team-reports-trust-msg">{t("teamReports.trustMsg")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3" data-testid="team-report-list">
          {all.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slatebody">
              {t("teamReports.noReports")}
              <p className="text-sm text-slatebody mt-3 max-w-md mx-auto leading-relaxed" data-testid="team-reports-empty-msg">{t("teamReports.emptyMsg")}</p>
            </div>
          )}
          {data.own.length > 0 && <p className="font-mono text-[11px] uppercase text-slatebody pt-1">{t("teamReports.yourReports")}</p>}
          {data.own.map((r) => <Item key={r.id} r={r} />)}
          {data.team.length > 0 && (
            <div className="flex items-center justify-between pt-3">
              <p className="font-mono text-[11px] uppercase text-slatebody">{t("teamReports.teamReports")}</p>
              <div className="flex items-center gap-3">
                {groupByTitle && (
                  <button type="button" onClick={exportRollupCsv} className="flex items-center gap-1.5 text-xs text-teal hover:text-teal-700 font-medium" data-testid="rollup-export-csv">
                    <Download className="h-3.5 w-3.5" /> {t("teamReports.exportCsv")}
                  </button>
                )}
                <label className="flex items-center gap-2 cursor-pointer" data-testid="group-by-title-row">
                  <input type="checkbox" checked={groupByTitle} onChange={(e) => setGroupByTitle(e.target.checked)}
                    className="h-4 w-4 accent-[#202123]" data-testid="group-by-title-checkbox" />
                  <span className="text-xs text-slatebody">{t("teamReports.groupByTitle")}</span>
                </label>
              </div>
            </div>
          )}
          {data.team.length > 0 && (groupByTitle
            ? (() => {
                const { groups, keys } = titleGroups();
                return (
                  <>
                    <p className="text-xs text-slatebody pt-1" data-testid="rollup-total">{t("teamReports.rollupTotal", { reports: data.team.length, titles: keys.length })}</p>
                    {keys.map((k) => (
                      <div key={k} className="space-y-3" data-testid={`title-group-${k === NONE ? "none" : k}`}>
                        <p className="font-mono text-[11px] uppercase text-slate-400 pt-2 pl-1" data-testid={`title-group-header-${k === NONE ? "none" : k}`}>
                          {k === NONE ? t("teamReports.noTitle") : k} · {t("teamReports.groupCount", { count: groups[k].length })}
                        </p>
                        {groups[k].map((r) => <Item key={r.id} r={r} />)}
                      </div>
                    ))}
                  </>
                );
              })()
            : data.team.map((r) => <Item key={r.id} r={r} />)
          )}
        </div>

        <div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 sticky top-6">
            <h3 className="font-display font-semibold text-navy mb-4">{t("teamReports.compileRollup")}</h3>
            <p className="text-sm text-slatebody mb-4">{t("teamReports.reportsSelected", { count: selected.length })}</p>
            <div className="space-y-4">
              <div>
                <Label>{t("teamReports.titleLabel")} <span className="text-slatebody font-normal">({t("common.optional")})</span></Label>
                <Input value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} className="mt-1.5" placeholder={t("teamReports.titlePlaceholder")} data-testid="team-title" />
              </div>
              <div>
                <Label>{t("reports.style")}</Label>
                <Select value={cfg.style} onValueChange={(v) => setCfg({ ...cfg, style: v })}>
                  <SelectTrigger className="mt-1.5 h-auto min-h-10" data-testid="team-style"><span className="text-left">{styleTriggerLabel(t, cfg.style)}</span></SelectTrigger>
                  <SelectContent>
                    <ReportStyleOptions t={t} customTestId="team-style-custom" />
                  </SelectContent>
                </Select>
                {cfg.style === CUSTOM_STYLE && <CustomTemplatePanel api={api} onChange={setCustomTpl} />}
              </div>
              <div>
                <Label>{t("reports.length")}</Label>
                <Select value={cfg.length} onValueChange={(v) => setCfg({ ...cfg, length: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="team-length"><SelectValue /></SelectTrigger>
                  <SelectContent>{LENGTHS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-slate-50 px-3.5 py-3 cursor-pointer" data-testid="team-include-scope-row">
                <input type="checkbox" checked={includeScope} onChange={(e) => setIncludeScope(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#202123]" data-testid="team-include-scope-checkbox" />
                <span className="text-sm text-navy">{t("teamReports.useScope")}
                  <span className="block text-xs text-slatebody font-normal mt-0.5">{t("teamReports.useScopeHint")}</span>
                </span>
              </label>
              <Button className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" disabled={compiling || selected.length === 0} onClick={compile} data-testid="team-compile-btn">
                <Sparkles className="h-4 w-4" /> {compiling ? t("teamReports.compiling") : t("teamReports.compile")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
